import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, MailCheck } from "lucide-react";
import { formatApiError } from "@/lib/errors";
import api from "@/lib/api";

/**
 * Password reset — step 1: request the reset link.
 *
 * Server always returns the same generic "if this email is registered…"
 * response for privacy. We reflect that in the confirmation screen too.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err) || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-900 font-mono-data flex items-center gap-1 mb-6"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to sign in
        </button>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <div className="w-12 h-12 rounded-sm bg-emerald-100 flex items-center justify-center mb-4">
              <MailCheck className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-mono-data">
              Check your inbox
            </div>
            <h2 className="text-2xl font-display font-bold tracking-tight text-slate-900">
              Reset link on its way
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              If <strong className="text-slate-900 break-all">{email}</strong> is
              registered, we've sent a link to reset your password. It expires in
              60 minutes.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Not seeing it? Check your spam folder, or wait a minute and try
              again.
            </p>
            <Button
              type="button"
              onClick={() => {
                setSent(false);
                setEmail("");
              }}
              variant="outline"
              className="mt-6 w-full rounded-sm h-10"
              data-testid="try-different-email"
            >
              Try a different email
            </Button>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-mono-data">
              Reset password
            </div>
            <h2 className="text-2xl font-display font-bold tracking-tight text-slate-900">
              Forgot your password?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter the email on your account and we'll send you a link to set a
              new one.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label
                  htmlFor="forgot-email"
                  className="text-xs uppercase tracking-wider"
                >
                  Email
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="mt-1.5 rounded-sm"
                  data-testid="forgot-password-email"
                />
              </div>

              {error ? (
                <div
                  className="text-sm text-destructive bg-red-50 border border-red-200 px-3 py-2 rounded-sm"
                  data-testid="forgot-password-error"
                >
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 group"
                data-testid="forgot-password-submit"
              >
                {submitting ? "Sending…" : "Send reset link"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </form>

            <div className="mt-10 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data text-center">
              <Link
                to="/login"
                className="hover:text-slate-900 underline-offset-2 hover:underline"
              >
                Return to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
